import axios from 'axios';

import { ComprasEExportHoursResponse, ComprasEExportIssueRow } from '../../types';
import { formatMonthYear } from '../../utils/monthYearParse';
import {
  hasCompletePmoWorklogPeriod,
  pmoPeriodToWorklogIsoDates,
} from './pmoWorklogPeriodBounds';
import { FULL_WORKLOG_PERIOD_EXPORT_TIMEOUT_MS } from './projectCostScope';

export const COMPRAS_E_DEFAULT_PERIOD_START = '01/2025';

export type ComprasEPackFilter = 'all' | 'portal' | 'cmpe';

export const COMPRAS_E_PACK_OPTIONS: Array<{ id: ComprasEPackFilter; label: string }> = [
  { id: 'all', label: 'Todas' },
  { id: 'portal', label: 'Portal de Compras [Compras-e]' },
  { id: 'cmpe', label: 'CMPE / COMPRAS-E' },
];

export type ComprasEExportKpis = {
  issueCount: number;
  parentCount: number;
  sapHours: number;
  portalHours: number;
  cmpeHours: number;
  totalHours: number;
  worklogCount: number;
  soldHours: number | null;
  consumptionPercent: number | null;
  subtaskCount: number;
  subtaskNaoIniciadoCount: number;
  subtaskTaggedCount: number;
  ownCostCents: number;
  thirdPartyCostCents: number;
  totalCostCents: number;
};

export const currentMonthYear = (now = new Date()): string =>
  formatMonthYear(now.getMonth() + 1, now.getFullYear());

export const shouldFetchComprasEExportHours = (periodStart: string, periodEnd: string): boolean => {
  if (!hasCompletePmoWorklogPeriod(periodStart, periodEnd)) return false;
  const iso = pmoPeriodToWorklogIsoDates(periodStart, periodEnd);
  if (!iso.worklogStartDate || !iso.worklogEndDate) return false;
  return iso.worklogStartDate <= iso.worklogEndDate;
};

export const soldHoursForComprasEPack = (
  pack: ComprasEPackFilter,
  soldHours: number | null | undefined,
): number | null => (pack === 'all' ? (soldHours ?? null) : null);

export const shouldShowComprasESoldKpis = (pack: ComprasEPackFilter): boolean => pack === 'all';

export const comprasEEsteiraBadgeLabel = (
  issue: Pick<ComprasEExportIssueRow, 'esteira' | 'project_key'>,
): string => {
  const esteira = String(issue.esteira || '').toLowerCase();
  const project = String(issue.project_key || '').toUpperCase();
  if (esteira === 'cmpe' || project === 'CMPE' || project === 'COMPRAS-E') return 'CMPE';
  return 'Portal de Compras';
};

export const parentJiraBrowseUrl = (
  issue: Pick<ComprasEExportIssueRow, 'parent_key' | 'jira_browse_url'>,
): string => {
  const parentKey = String(issue.parent_key || '').trim();
  if (!parentKey) return '';
  const current = String(issue.jira_browse_url || '');
  if (!current) return '';
  return current.replace(/\/browse\/[^/]+$/, `/browse/${parentKey}`);
};

const normalizeSearch = (value: string): string => value.trim().toLowerCase();

const inferEsteira = (issue: ComprasEExportIssueRow): 'portal' | 'cmpe' => {
  const explicit = String(issue.esteira || '').toLowerCase();
  if (explicit === 'cmpe' || explicit === 'portal') return explicit;
  const project = String(issue.project_key || '').toUpperCase();
  return project === 'CMPE' || project === 'COMPRAS-E' ? 'cmpe' : 'portal';
};

export const filterComprasEExportIssues = (
  issues: ComprasEExportIssueRow[],
  pack: ComprasEPackFilter,
  search: string,
): ComprasEExportIssueRow[] => {
  const needle = normalizeSearch(search);
  return issues.filter((issue) => {
    if (pack !== 'all' && inferEsteira(issue) !== pack) {
      return false;
    }
    if (!needle) return true;
    const haystack = [
      issue.key,
      issue.summary,
      issue.parent_key,
      issue.parent_summary,
      issue.status,
      issue.project_key,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
};

export const COMPRAS_E_ALL_ISSUE_TYPES = 'all';

/** Tipos presentes no recorte atual, para montar o seletor acima da tabela. */
export const comprasEIssueTypeOptions = (
  issues: ComprasEExportIssueRow[] | null | undefined,
): string[] => {
  const seen = new Set<string>();
  const labels: string[] = [];
  (issues || []).forEach((issue) => {
    const label = String(issue.issue_type || '').trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    labels.push(label);
  });
  return labels.sort((left, right) => left.localeCompare(right, 'pt-BR'));
};

/** Filtro só da tabela: os cards continuam refletindo o recorte inteiro. */
export const filterComprasEIssuesByType = (
  issues: ComprasEExportIssueRow[],
  issueType: string,
): ComprasEExportIssueRow[] => {
  const wanted = String(issueType || '').trim().toLowerCase();
  if (!wanted || wanted === COMPRAS_E_ALL_ISSUE_TYPES) return issues;
  return issues.filter((issue) => String(issue.issue_type || '').trim().toLowerCase() === wanted);
};

const sumHours = (issues: ComprasEExportIssueRow[], pack?: 'sap' | 'nsap'): number =>
  issues.reduce((total, issue) => {
    if (pack && String(issue.pack || '').toLowerCase() !== pack) return total;
    return total + Number(issue.hours_in_period || 0);
  }, 0);

const sumEsteiraHours = (issues: ComprasEExportIssueRow[], esteira: 'portal' | 'cmpe'): number =>
  issues.reduce((total, issue) => {
    if (inferEsteira(issue) !== esteira) return total;
    return total + Number(issue.hours_in_period || 0);
  }, 0);

/** Tickets-pai distintos que tiveram filho apontado no recorte. Base do KPI "Demandas com apontamento". */
export const comprasEExportParentKeys = (
  issues: ComprasEExportIssueRow[] | null | undefined,
): string[] => {
  const pais = new Set<string>();
  (issues || []).forEach((issue) => {
    const pai = String(issue.parent_key || '').trim().toUpperCase();
    if (pai) pais.add(pai);
  });
  return Array.from(pais);
};

const countUniqueParents = (issues: ComprasEExportIssueRow[]): number =>
  comprasEExportParentKeys(issues).length;

const sumCents = (issues: ComprasEExportIssueRow[], field: 'own_cost_cents' | 'third_party_cost_cents' | 'cost_cents'): number =>
  issues.reduce((total, issue) => total + Number(issue[field] || 0), 0);

export const summarizeComprasEExportIssues = (
  issues: ComprasEExportIssueRow[],
  soldHours: number | null | undefined,
  subtaskCounts?: {
    subtaskCount?: number | null;
    subtaskNaoIniciadoCount?: number | null;
    subtaskTaggedCount?: number | null;
  },
): ComprasEExportKpis => {
  const sapHours = sumHours(issues, 'sap');
  const portalHours = sumEsteiraHours(issues, 'portal');
  const cmpeHours = sumEsteiraHours(issues, 'cmpe');
  const totalHours = portalHours + cmpeHours;
  const sold = soldHours == null ? null : Number(soldHours);
  const consumptionPercent = sold && sold > 0 ? (totalHours / sold) * 100 : null;
  const ownCostCents = sumCents(issues, 'own_cost_cents');
  const thirdPartyCostCents = sumCents(issues, 'third_party_cost_cents');
  return {
    issueCount: issues.length,
    parentCount: countUniqueParents(issues),
    sapHours,
    portalHours,
    cmpeHours,
    totalHours,
    worklogCount: issues.reduce((total, issue) => total + Number(issue.worklog_count || 0), 0),
    soldHours: sold,
    consumptionPercent,
    subtaskCount: Number(subtaskCounts?.subtaskCount || 0),
    subtaskNaoIniciadoCount: Number(subtaskCounts?.subtaskNaoIniciadoCount || 0),
    subtaskTaggedCount: Number(subtaskCounts?.subtaskTaggedCount || 0),
    ownCostCents,
    thirdPartyCostCents,
    totalCostCents: ownCostCents + thirdPartyCostCents,
  };
};

export const formatComprasEHours = (value: number): string =>
  value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatComprasECurrency = (cents: number): string =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const formatComprasEPercent = (value: number | null): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

export const fetchComprasEExportHoursPanel = async (
  apiUrl: string,
  worklogStartDate: string,
  worklogEndDate: string,
  timeoutMs = FULL_WORKLOG_PERIOD_EXPORT_TIMEOUT_MS,
): Promise<ComprasEExportHoursResponse> => {
  const response = await axios.post<ComprasEExportHoursResponse>(
    `${apiUrl}/api/projects/costs/compras-e-export-hours`,
    {
      worklog_start_date: worklogStartDate,
      worklog_end_date: worklogEndDate,
    },
    { timeout: timeoutMs },
  );
  return response.data;
};
